import { Eye, EyeOff } from 'react-feather';
import { isFuture, isToday } from 'date-fns';
import { useTranslation } from 'react-i18next';

import UI from '../../../../common/UI';
import useLocale from '../../../../common/useLocale';

interface Invitation {
  start_date?: string | null; // UTC
  end_date?: string | null; // UTC
  active: boolean;
  available: boolean;
}

export interface InvitationStatusIndicatorProps {
  invitation: Invitation;
}

const InvitationStatusIndicator = ({ invitation }: InvitationStatusIndicatorProps) => {
  const { status, message } = useInvitationStatus(invitation);

  const icons = {
    [InvitationStatus.Available]: <Eye />,
    [InvitationStatus.Disabled]: <EyeOff />,
    [InvitationStatus.UsedUp]: <EyeOff />,
    [InvitationStatus.StartsOn]: <EyeOff />,
    [InvitationStatus.EndsOn]: <Eye />,
    [InvitationStatus.EndedOn]: <EyeOff />,
  };

  const icon = icons[status];

  return (
    <>
      <UI.Icon>
        {icon}
      </UI.Icon>
      {' '}
      {message}
    </>
  );
};

export enum InvitationStatus {
  Available = 'available',
  Disabled = 'disabled',
  UsedUp = 'used_up',
  StartsOn = 'starts_on',
  EndsOn = 'ends_on',
  EndedOn = 'ended_on',
}

export const useInvitationStatus = (invitation: Invitation) => {
  const { t } = useTranslation();
  const { parseDate, formatDate } = useLocale();

  const status = (status: InvitationStatus, message: string, date: string = undefined) => ({
    status, message, date,
  });

  if (!invitation.active) {
    return status(InvitationStatus.Disabled, t('disabled'));
  }

  if (!invitation.available) {
    return status(InvitationStatus.UsedUp, t('used_up'));
  }

  if (invitation.start_date) {
    const startDate = parseDate(invitation.start_date, { timezone: 'UTC' });

    if (isFuture(startDate)) {
      const date = formatDate(
        startDate,
        { format: isToday(startDate) ? 'display_time' : 'display_compact_date' },
      );

      return status(InvitationStatus.StartsOn, t('starts_on', { date }), date);
    }
  }

  if (invitation.end_date) {
    const endDate = parseDate(invitation.end_date, { timezone: 'UTC' });

    const date = formatDate(
      endDate,
      { format: isToday(endDate) ? 'display_time' : 'display_compact_date' },
    );

    if (isFuture(endDate)) {
      return status(InvitationStatus.EndsOn, t('ends_on_date', { date }), date);
    }

    return status(InvitationStatus.EndedOn, t('ended_on', { date }), date);
  }

  return status(InvitationStatus.Available, t('available'));
};

export default InvitationStatusIndicator;
